import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';
import {push} from 'react-router-redux';

import Page from 'components/Page';
import ListContent from 'components/ListContent';
import VisibleIf from 'components/VisibleIf';

import {Container} from 'containers/types';
import Loading from 'components/Loading';
import {
  default as LoginRequired,
  LoginRequiredProps
} from 'components/LoginRequired';
import {
  withPagination,
  PAGINATION,
  WithPaginationProps
} from 'components/withPagination';
import {withFilters, FILTERS, WithFiltersProps} from 'components/withFilters';

import {SubNavigation, MembersTable, MemberListFilters} from './components';

import {reverse} from 'utils/urls';
import {
  getUpdatedFilter,
  transformFiltersForStoreToFiltersForApi
} from 'utils/filters';
import {getFormattedDateForApiOrValue} from 'utils/datetime';
import {MEMBER_DETAILS_URL} from 'config/urls';

import {composeActions} from 'ducks/common';
import {fetchAllRooms, selectAllRooms} from 'ducks/rooms';
import {
  fetchMembersForDashboard,
  selectMembers,
  selectMembersLoading
} from 'ducks/users';
import {exportCsvInBackground, selectExporting} from 'ducks/files';
import {
  requiresPermission,
  requiresMultiplePermissions,
  PERMISSIONS
} from 'ducks/auth';

import {exportMembersCsv as exportMembersCsvSdk} from 'sdk/users';

import './styles.css';

const MEMBERS_CSV = 'members';

const initialFilters: {
  search: string;
  rooms: number[];
  arrivalAfter: string;
  arrivalBefore: string;
  departureAfter: string;
  departureBefore: string;
  bookingSetStatuses: string[];
} = {
  search: '',
  rooms: [],
  arrivalAfter: '',
  arrivalBefore: '',
  departureAfter: '',
  departureBefore: '',
  bookingSetStatuses: []
};

class MemberList extends React.Component<
  Container &
    LoginRequiredProps &
    WithFiltersProps<typeof initialFilters> &
    WithPaginationProps &
    ReturnType<typeof mapStateToProps> &
    typeof mapDispatchToProps
> {
  componentDidMount() {
    const {match: {params: {locationRef}}} = this.props;

    this.props.requiresPermission(
      {
        locationRef,
        permission: PERMISSIONS.VIEW_MEMBERS_LIST
      },
      () => {
        this.props.fetchAllRooms({locationRef});
        this.props.fetchMembersForDashboard({locationRef});
      }
    );
  }

  clearFilters = () => {
    const {locationRef} = this.props.match.params;

    this.props.composeActions([
      this.props.clearFilters,
      () => fetchMembersForDashboard({locationRef})
    ]);
  };

  setFilter = (field, value) => {
    const {locationRef} = this.props.match.params;
    const {pageSize} = this.props.getPagination();

    const filters = this.props.getFilters();
    const updatedFilter = getUpdatedFilter({field, value}, filters);

    this.props.composeActions([
      () => this.props.setFilter(updatedFilter.field, updatedFilter.value),
      () => this.props.onPaginationChange({offset: 0, pageSize}),
      () => fetchMembersForDashboard({locationRef})
    ]);
  };

  onPageChange = ({offset, pageSize}) => {
    const {match: {params: {locationRef}}} = this.props;

    this.props.onPaginationChange({offset, pageSize}, [
      () => fetchMembersForDashboard({locationRef})
    ]);
  };

  exportMembersCsv = () => {
    const {locationRef} = this.props.match.params;

    const formattedFilters = _.mapValues(
      this.props.getFilters(),
      getFormattedDateForApiOrValue
    );
    const filtersForApi = transformFiltersForStoreToFiltersForApi(
      formattedFilters
    );

    this.props.requiresMultiplePermissions(
      {
        permissions: [
          {
            locationRef,
            permission: PERMISSIONS.EXPORT_MEMBERS_LIST
          },
          {
            permission: PERMISSIONS.VIEW_CSV_EXPORT_FILE
          }
        ],
        disableClose: true
      },
      () =>
        this.props.exportCsvInBackground({
          exportData: {
            locationRef,
            filters: filtersForApi
          },
          exportSdk: exportMembersCsvSdk,
          fileName: MEMBERS_CSV
        })
    );
  };

  openMembersProfilePage = memberId => {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {locationRef, permission: PERMISSIONS.VIEW_MEMBER_PROFILE},
      () =>
        this.props.push(
          reverse(MEMBER_DETAILS_URL, {
            locationRef,
            memberId
          })
        )
    );
  };

  render() {
    const {
      match: {params: {locationRef}},
      rooms,
      members,
      membersLoading,
      exportingMembersCsv
    } = this.props;

    return (
      <Page activeTab="membersList" locationRef={locationRef}>
        <VisibleIf
          permission={PERMISSIONS.VIEW_MEMBERS_LIST}
          locationRef={locationRef}>
          <ListContent
            className="MemberList"
            filters={this.props.getFilters()}
            setFilter={this.setFilter}
            initialFilters={initialFilters}
            navigationExtraElement={SubNavigation}>
            <ListContent.Content>
              {membersLoading && <Loading />}
              {!membersLoading &&
                !_.isEmpty(members) && (
                  <div className="MemberList__content">
                    <MembersTable
                      members={members}
                      pagination={this.props.getPagination()}
                      onPageChange={this.onPageChange}
                      exportMembersCsv={this.exportMembersCsv}
                      exportingMembersCsv={exportingMembersCsv}
                      disableExport={_.isEmpty(members)}
                      openMembersProfilePage={this.openMembersProfilePage}
                    />
                  </div>
                )}
            </ListContent.Content>
            <ListContent.Filters>
              <MemberListFilters
                setFilter={this.setFilter}
                getFilters={this.props.getFilters}
                clearFilters={this.clearFilters}
                rooms={rooms}
              />
            </ListContent.Filters>
          </ListContent>
        </VisibleIf>
      </Page>
    );
  }
}

const mapStateToProps = state => ({
  rooms: selectAllRooms(state),
  members: selectMembers(state),
  membersLoading: selectMembersLoading(state),
  exportingMembersCsv: selectExporting(state, MEMBERS_CSV)
});

const mapDispatchToProps = {
  fetchAllRooms,
  fetchMembersForDashboard,
  composeActions,
  exportCsvInBackground,
  requiresPermission,
  requiresMultiplePermissions,
  push
};

const componentWithPagination = withPagination(
  MemberList,
  PAGINATION.MEMBER_LIST,
  {
    saveToUrl: true
  }
);

const componentWithFiltersAndPagination = withFilters(
  componentWithPagination,
  FILTERS.MEMBER_LIST,
  {initialFilters, saveToUrl: true}
);

export default connect(mapStateToProps, mapDispatchToProps)(
  LoginRequired(componentWithFiltersAndPagination)
);
